<template>
  <div class="col-12" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('salary_management.add_payment') }}
            </h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2">
        <div class="p-4">
          <form @submit.prevent="submit" @keydown.enter.prevent>
            <div class="row g-3">

              <div class="col-md-6">
                <label class="bmd-label-float">{{ $t('salary_management.employee_type') }} *</label>
                <select
                  class="form-select borderless-input"
                  :class="{ 'is-invalid': errors.modelType }"
                  v-model="form.modelType"
                  @change="loadEmployees"
                  required
                >
                  <option value="">{{ $t('salary_management.select_type') }}</option>
                  <option value="teacher">{{ $t('salary_management.teachers') }}</option>
                  <option value="employee">{{ $t('salary_management.employees') }}</option>
                  <option value="user">{{ $t('salary_management.users') }}</option>
                </select>
                <div class="invalid-feedback">{{ errors.modelType }}</div>
              </div>

              <div class="col-md-6">
                <label class="bmd-label-float">{{ $t('salary_management.employee') }} *</label>
                <select
                  class="form-select borderless-input"
                  :class="{ 'is-invalid': errors.modelId }"
                  v-model="form.modelId"
                  :disabled="!form.modelType"
                  @change="onEmployeeSelected"
                  required
                >
                  <option value="">{{ $t('salary_management.select_employee') }}</option>
                  <option
                    v-for="e in employees"
                    :key="e.id"
                    :value="e.id"
                  >
  {{ e.name }} ({{ e.displaySalary }} {{ $t('common.currency') }}{{ e.salaryTypeNote ? ' - ' + e.salaryTypeNote : '' }})
                  </option>
                </select>
                <div class="invalid-feedback">{{ errors.modelId }}</div>
              </div>

              <div class="col-md-6">
                <label class="bmd-label-float">{{ $t('salary_management.payment_type') }} *</label>
                <select
                  class="form-select borderless-input"
                  :class="{ 'is-invalid': errors.type }"
                  v-model="form.type"
                  required
                >
                  <option value="">{{ $t('salary_management.select_type') }}</option>
                  <option value="salary">{{ $t('salary_management.salary') }}</option>
                  <option value="on_account">{{ $t('salary_management.on_account') }}</option>
                </select>
                <div class="invalid-feedback">{{ errors.type }}</div>
              </div>

              <div class="col-md-6">
                <label class="bmd-label-float">{{ $t('salary_management.value') }} *</label>
                <input
                  type="number"
                  step="0.01"
                  class="form-control borderless-input"
                  :class="{ 'is-invalid': errors.value }"
                  v-model.number="form.value"
                  required
                />
                <div class="invalid-feedback">{{ errors.value }}</div>
                <small v-if="loadingBalance" class="text-muted d-block mt-1">
                  <i class="fas fa-spinner fa-spin me-1"></i>{{ $t('salary_management.loading_balance') }}
                </small>
                <small v-else-if="selectedBalance !== null" class="d-block mt-1" :class="selectedBalance > 0 ? 'text-success' : 'text-muted'">
                  <i class="fas fa-info-circle me-1"></i>
                  {{ $t('salary_management.outstanding_balance') }}: {{ displayNoThousands(selectedBalance) }} {{ $t('common.currency') }}
                </small>
              </div>

              <div class="col-md-6">
                <label class="bmd-label-float">{{ $t('salary_management.payment_date') }} *</label>
                <input
                  type="date"
                  class="form-control borderless-input"
                  :class="{ 'is-invalid': errors.payment_date }"
                  v-model="form.payment_date"
                  required
                />
                <div class="invalid-feedback">{{ errors.payment_date }}</div>
              </div>

              <div class="col-12">
                <label class="bmd-label-float">{{ $t('salary_management.description') }}</label>
                <textarea
                  class="form-control borderless-input"
                  rows="3"
                  :placeholder="$t('salary_management.payment_description_placeholder')"
                  v-model.trim="form.description"
                ></textarea>
              </div>

            </div>
          </form>

          <div class="d-flex gap-3 mt-4">
            <button
              type="button"
              class="btn btn-primary btn-sm flex-grow-1"
              @click="submit"
              :disabled="submitting"
            >
              <span v-if="submitting">
                <i class="fas fa-spinner fa-spin me-2"></i>{{ $t('common.saving') }}
              </span>
              <span v-else>
                <i class="fas fa-save me-2"></i>{{ $t('salary_management.pay') }}
              </span>
            </button>

            <button
              type="button"
              class="btn btn-outline-secondary btn-sm flex-grow-1"
              @click="goBack"
              :disabled="submitting"
            >
              <i class="fas fa-times me-2"></i>{{ $t('common.cancel') }}
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import api from '@/api';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const toast = useToast();

const submitting = ref(false);
const employees = ref([]);
const selectedBalance = ref(null);
const loadingBalance = ref(false);

const form = ref({
  modelType: '',
  modelId: '',
  type: '',
  value: null,
  payment_date: new Date().toISOString().split('T')[0],
  description: ''
});
const errors = ref({});

// --- helpers to prevent NaN ---
const toNumber = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  // strip everything except digits, dot, minus
  const cleaned = String(v).replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};
const displayNoThousands = (n) => {
  const x = toNumber(n);
  return Number.isInteger(x) ? String(x) : String(x.toFixed(2)).replace(/\.?0+$/, '');
};

// Non-teaching staff accounts (finance, academic managers, student affairs,
// school admin, ...) are payroll-eligible too. Students/guardians/teachers
// are excluded — teachers already have their own dedicated model type.
const NON_PAYROLL_ROLES = ['teacher', 'student', 'guardian'];

const loadEmployees = async () => {
  employees.value = [];
  form.value.modelId = '';
  selectedBalance.value = null;
  if (!form.value.modelType) return;

  try {
    if (form.value.modelType === 'user') {
      // /users has no pagination and returns a plain (unwrapped) array.
      const res = await api.get('/users');
      const list = (Array.isArray(res.data) ? res.data : []).filter(
        (u) => !NON_PAYROLL_ROLES.includes(u.role?.slug)
      );
      employees.value = list.map((u) => {
        const numericSalary = toNumber(u.salary ?? u.deserved_salary ?? 0);
        return {
          id: u.id,
          name: u.name || u.email || `#${u.id}`,
          salary: numericSalary,
          displaySalary: displayNoThousands(numericSalary),
        };
      });
      return;
    }

    const endpoint = form.value.modelType === 'teacher' ? '/teachers' : '/employees';
    const res = await api.get(endpoint, { params: { per_page: 1000 } });
    const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);

    // Teachers paid per lesson (or fixed+lessons) don't have a plain fixed
    // amount — surface that in the dropdown so admins don't mistake the
    // shown figure for a normal flat salary.
    const salaryTypeLabels = {
      per_lesson: t('teachers.salaryTypePerLesson'),
      fixed_plus_lessons: t('teachers.salaryTypeFixedPlusLessons'),
    };

    employees.value = list.map(emp => {
      // try multiple sources for salary
      const rawSalary =
        emp.salary ??
        emp.deserved_salary ??
        emp.formatted_salary ??
        emp.formatted_deserved_salary ??
        0;

      const numericSalary = toNumber(rawSalary);
      const salaryTypeNote = salaryTypeLabels[emp.salary_type] || '';

      return {
        id: emp.id,
        name: emp.username || emp.name || emp.email || `#${emp.id}`,
        salary: numericSalary,
        displaySalary: displayNoThousands(numericSalary),
        salaryTypeNote,
      };
    });
  } catch (_) {
    employees.value = [];
    toast.error(t('common.error'));
  }
};

// When a person is selected, look up their latest ledger balance
// (SalaryStatement.balance = total credited "salary due"/rewards minus
// debited payments/deductions — i.e. exactly what is still owed to them)
// and use it to suggest the payment amount. This works uniformly across
// Teacher/Employee/User since it reads from the shared statements ledger
// rather than any type-specific "salary" column.
const onEmployeeSelected = async () => {
  selectedBalance.value = null;
  if (!form.value.modelType || !form.value.modelId) return;

  loadingBalance.value = true;
  try {
    const res = await api.get(
      `/salary-management/${form.value.modelType}/${form.value.modelId}/statements`,
      { params: { per_page: 1 } }
    );
    const list = res.data?.data || [];
    const balance = list.length ? toNumber(list[0].balance) : 0;
    selectedBalance.value = balance;
    if (balance > 0) {
      form.value.value = balance;
    }
  } catch (_) {
    selectedBalance.value = null;
  } finally {
    loadingBalance.value = false;
  }
};

const validate = () => {
  errors.value = {};
  if (!form.value.modelType) errors.value.modelType = t('validation.required');
  if (!form.value.modelId) errors.value.modelId = t('validation.required');
  if (!form.value.type) errors.value.type = t('validation.required');
  if (!form.value.value || toNumber(form.value.value) <= 0) errors.value.value = t('validation.positive_number');
  if (!form.value.payment_date) errors.value.payment_date = t('validation.required');
  return Object.keys(errors.value).length === 0;
};

const submit = async () => {
  if (!validate()) return;
  submitting.value = true;
  try {
    const { modelType, modelId, ...payload } = form.value;
    payload.value = toNumber(payload.value); // normalize
    await store.dispatch('salaryManagement/paySalary', { modelType, modelId, data: payload });
    toast.success(t('salary_management.payment_added'));
    router.push({ name: 'salary.management' });
  } catch (e) {
    toast.error(e?.response?.data?.message || t('salary_management.payment_error'));
  } finally {
    submitting.value = false;
  }
};

const goBack = () => router.push({ name: 'salary.management' });
</script>
